import { IOrderBy } from "../../../../Global/Base/DataSources/Interfaces";
import { Control } from "../../../UI.BaseControls/Control";
import { createElement, getScrollBarWidth, registerCustomElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { DataGrid } from "../../DataGrid";
import { DataGridSharedMethods } from "../../DataGridSharedMethods";
import { IDataGrid } from "../../IDataGrid";
import { Column } from "../Columns/Column";
import { HeaderCell } from "./HeaderCell";
import { IDataRowElement } from "./IDataRow";
import { IHeaderRow } from "./IHeaderRow";

export class HeaderRow extends Control implements IHeaderRow<IDataRowElement> {
    //#region Events
    //#endregion Events

    //#region Listeners
    //#endregion Listeners

    //#region Fields
    protected _choiceElement: HTMLElement;
    protected _frozenPart: HTMLElement;

    public setChoiceElementWidth(value: number): void {
        if (this._choiceElement)
            this._choiceElement.style.width = value + "px";
    }

    public getChoiceElementWidth(): number {
        if (this._choiceElement) {
            const computedStyles = getComputedStyle(this._choiceElement);

            return this._choiceElement.offsetWidth + parseInt(computedStyles.marginLeft) + parseInt(computedStyles.marginRight);
        }
        return 0;
    }

    /**
     * @returns Returns width of all column (with choice element and frozen part)
     */
    public getAllColumnsWidth(): number {
        const allColumnsArr = Array.from(this._frozenPart.children).concat(Array.from((this.children)));
        const widths = allColumnsArr.map((child: HTMLElement, index) => {
            if (index === 0) { // choice element is in the first position in both this.children and this._frozenPart.children, so it is ignored in the first position and counted only in this.children
                return 0;
            } else if (index === allColumnsArr.length - 1) { // In the last position is a substitute element for the scrollbar, which we don't want to count
                return 0;
            } else {
                return child.offsetWidth;
            }
        });

        return widths.reduce(function(accumulator, currentValue) {
            return accumulator + currentValue;
        }, 0);
    }

    protected _headerCells: HeaderCell[];

    /**
     * Uchovává reference na vytvořené buňky hlavičky pro účely dispose.
     * @private
     */
    private _headerCellsForDispose: HeaderCell[] = [];

    public width: number;

    protected _owner: IDataGrid<IDataRowElement>;

    private _disableSort: boolean = false;
    //#endregion Fields

    //#region Properties
    public get extraClasses(): string[] {
        return ["with-control-cell"];
    }

    private _color: string = "";
    private _scrollBarBuffer: HTMLElement;

    public set color(value: string) {
        if (this._color && this._color.length > 0)
            this.classList.remove(this._color);

        this._color = value;

        //this.headerRow.element.style.backgroundColor = value;
        this.classList.add(value);
    }

    public get color(): string {
        return this._color;
    }

    public set frameMenuVisibility(v: boolean) {
        this._choiceElement.style.visibility = v ? "" : "hidden";
    }

    public get disableSort(): boolean {
        return this._disableSort;
    }
    public set disableSort(v: boolean) {
        this._disableSort = v;
    }

    public set scrollBarBufferVisible(v: boolean) {
        if (!this._scrollBarBuffer) {
            return;
        }

        if (v) {
            this._scrollBarBuffer.style.display = "block";
        } else {
            this._scrollBarBuffer.style.display = "none";
        }
    }
    //#endregion Properties

    //#region Constructors
    //#endregion Constructors

    //#region Methods
    /**
     * Vytvoří hlavičky sloupců
     * @param owner - datagrid, kterému tyto sloupce patří
     * @param columns - seznam sloupců
     * @param orderBy
     * @param startIndex - index, od kterého jsou číslovány sloupce
     * @param addNoSortItem - příznak určující, zda se má dorovnávat šířka scrollbaru
     */
    public render(owner: IDataGrid<IDataRowElement>, columns: Array<Column>, orderBy: IOrderBy[], startIndex: number = 0, addNoSortItem: boolean = true): void {
        const isDynamic = (startIndex > 0);

        if (!isDynamic) {
            this._owner = owner;
            this.width = this._choiceElement.clientWidth; // kvuli prvni bunce (fitrovani - neni mezi sloupci)
            this._clearHeader();
        }

        for (const column of columns) {
            const headerCell = new HeaderCell(this._owner, startIndex, column);

            headerCell.visibility = column.visible;
            this.width += headerCell.column.width;

            const indexOf = DataGridSharedMethods.orderByIndexOf(orderBy, column.orderAttrName);
            const root = column.isFrozen ? this._frozenPart : this;

            if (indexOf > -1) {
                root.appendChild(headerCell.render(orderBy[indexOf].order));
            } else {
                root.appendChild(headerCell.render(null));
            }

            startIndex++;
            this._headerCellsForDispose.push(headerCell);
        }

        // dorovnava sirku scrollbaru, aby neodjizdela hlavicka pokud doscroluju horizontalnim scrollbarem nakoneca je zobrazen verticalni scrollbar
        if (isDynamic) {
            this.removeChild(this._scrollBarBuffer);
        } else {
            if (addNoSortItem) {
                this._scrollBarBuffer = createElement({ css: { width: getScrollBarWidth() + "px", height: "100%" }, classes: "no-sort-item" });
            }
        }

        this.appendChild(this._scrollBarBuffer);

        this.widgetSortable(true, { axis: "x", handle: ".sort-drag-handle", items: "> *:not(.no-sort-item)" });
    }

    /**
     * Přerenderování kvůli změně orderby.
     * @param owner Seznam, který se změnil
     */
    public renderOrderBy(owner: DataGrid<IDataRowElement>): void {
        this._owner = owner;
        if (!this._headerCells)
            this._headerCells = [];

        for (let i = 0; i < owner.columns.length; i++) {
            const column: Column = owner.columns[i];

            if (column.visible) {
                const headerCell = new HeaderCell(owner, i, column);
                const indexOf = DataGridSharedMethods.orderByIndexOf(owner.orderBy, column.orderAttrName);

                if (indexOf > -1) {
                    headerCell.render(owner.orderBy[indexOf].order);
                } else {
                    headerCell.render(null);
                }
            }
        }
    }

    /**
     * Vloží falešné sloupce, které doplní délku po chybejicích normálních, ktere jsou ve frozenColumn
     * @param values
     */
    public addFakeCell(values: Iterable<HTMLElement>): void {
        for (const val of values) {
            if (this._choiceElement.nextElementSibling == null)
                this._choiceElement.appendChild(val);
            else
                this.insertBefore(val, this._choiceElement.nextElementSibling);
        }
    }

    public removeFakeCells(): void {
        this.querySelectorAll(".fake-header-cell").forEach((el) => {
            el.remove();
        });
    }

    public recountWidth(columns: Array<Column>): void {
        this.width = this._choiceElement.clientWidth;
        for (let i = 0; i < columns.length; i++) {
            this.width += columns[i].width;
        }
    }

    /** Odstraní všechna pole hlavičky kromě nastavení */
    protected _clearHeader(): void {
        const childrenToDelete = this.querySelectorAll(".HeaderCell:not(.header-framemenu-button)");
        for (const child of childrenToDelete) {
            child.remove();
        }
    }
    //#endregion Methods

    //#region HTMLElement
    protected override _constructor(): void {
        super._constructor();

        this.classList.add("headerRow");

        this._frozenPart = createElement({ classes: "frozen-part no-sort-item" });
        this.insertAdjacentElement("afterbegin", this._frozenPart);

        //prvni bunka v radku - start
        this._choiceElement = document.createElement("th");
        this._frozenPart.appendChild(this._choiceElement);
        this._choiceElement.style.visibility = "hidden";
        this._choiceElement.classList.add("HeaderCell", "header-framemenu-button", "icon", "settings-equalizer");
        this._choiceElement.setAttribute("data-id", "sl0");

        this._choiceElement.addEventListener("click", (e) => {
            if (!this._owner.interactive) {
                return;
            }

            this._choiceElement.dispatchEvent(new CustomEvent("openFrameMenuClicked", { detail: e }));
        });
        //prvni bunka v radku - end
    }

    // You can also define the other lifecycle methods.
    protected override connectedCallback(): void {
        super.connectedCallback();
    }

    protected override disconnectedCallback(): void {
        super.disconnectedCallback();
    }

    public override dispose(): void {
        this._headerCellsForDispose?.forEach((headerCell) => headerCell.dispose());
        this._headerCellsForDispose = [];

        super.dispose();
    }

//#endregion HTMLElement
}

registerCustomElement("tsc-headerrow", HeaderRow);
